import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetDistributionMetricByRuleId } from "@/services";
import { useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

const useDetailedView = () => {
  const { id, type, glId } = useParams();
  const { communityData, metricData: _metricData } = useSelector(
    (state: RootState) => state.currentData
  );
  const tableRef = useRef<any>(null);
  const [isAllExpanded, setIsAllExpanded] = useState(false);
  const location = useLocation();
  const isConfig = location.pathname?.includes(routes.distributionRuleConfiguration);
  const fromTraceability = location.pathname?.includes(routes.traceability);
  
  const communityName =
    communityData?.communityName || communityData?.name || "";

  const {data: metricData, isLoading: isMetricDataLoading} = useGetDistributionMetricByRuleId({
    communityId: communityData?.communityId,
    ruleId: id as string,
    queryParams: {
      enabled: !!id, 
    }
  });

  const metricBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionMetricsMaintenance}`,
        name: `Allocation Metrics Maintenance`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">{_metricData?.ruleName || "Weighing Factor"}</span>
        ),
      },
    ],
    [communityName, _metricData]
  );

  const configureBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}`,
        name: `Cost Analytics`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionRuleConfiguration}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.costAnalytics}/${routes.distributionRuleConfiguration}/${routes.weighingFactor}/${type}`,
        name: `Weighing Factor`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">{_metricData?.ruleName || "Weighing Factor"}</span>
        ),
      },
    ],
    [communityName, _metricData]
  );

  const traceabilityBreadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.bom}/${routes.finance}`,
        name: `Activity Dashboard - ${communityName}`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}`,
        name: `Traceability`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}/${routes.distributionRuleConfiguration}/${glId}`,
        name: `Allocation Rule By Level`,
      },
      {
        link: `/${routes.bom}/${routes.finance}/${routes.traceability}/${routes.distributionRuleConfiguration}/${glId}/${routes.weighingFactor}/${type}`,
        name: `Weighing Factor`,
      },
      {
        link: "",
        name: (
          <span className="text-primary">{_metricData?.ruleName || "Weighing Factor"}</span>
        ),
      },
    ],
    [communityName, _metricData]
  );

  const breadcrumbData = fromTraceability ? traceabilityBreadcrumbData : isConfig ? configureBreadcrumbData : metricBreadcrumbData;

  const title = useMemo(() => _metricData?.ruleName || "Weighing Factor", [_metricData]);

  const tableData = useMemo(() => {
    const nodes = metricData?.data?.nodes;
    if (!nodes || !Array.isArray(nodes)) return [];
  
    // 1. Group items by their parentName for O(1) fast lookup performance
    const parentMap = new Map<string, any[]>();
    
    nodes.forEach(node => {
      // We normalize keys to lower case to eliminate casing bugs
      const parentKey = node.parentName ? node.parentName.toLowerCase() : "root";
      if (!parentMap.has(parentKey)) {
        parentMap.set(parentKey, []);
      }
      const _node = parentKey == "root" ? {...node, parentName: communityName} : {...node}
      parentMap.get(parentKey)!.push({ ..._node, children: [] });
    });
  
    // 2. Recursive function to construct multi-level children branches
    const buildTree = (parentNameKey: string): any[] => {
      const directChildren = parentMap.get(parentNameKey) || [];
      
      return directChildren.map(child => {
        // Find and nest deeper grand-children recursively
        const childNameKey = child.name.toLowerCase();
        if (parentMap.has(childNameKey)) {
          child.children = buildTree(childNameKey);
        }
        return child;
      });
    };
  
    // 3. Initiate the generation step from the absolute root level elements
    return buildTree("root");
  }, [metricData]);

  // Define Table Columns
  const columns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name}`,
      size: 100
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>,
      size: 100
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      cell: ({row}: any) => row?.original?.metricValue ? Number(row?.original?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
      size: 100
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: ({row}: any) => {
        const isActive = row?.original?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  // Define Table Columns
  const subColumns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name}`,
      size: 105
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>,
      size: 115
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => row?.metricValue ? Number(row?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
      size: 100
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        const isActive = row?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  // Define Table Columns
  const level2Columns = [
    {
      accessorKey: "name",
      header: <div>Level</div>,
      accessorFn: (row: any) => `${row?.level}-${row?.name?.split(" - ")?.[0] || ""}`,
      size: 95
    },
    {
      accessorKey: "parentName",
      header: <div>Parent</div>
    },
    {
      accessorKey: "type",
      header: <div>Type</div>
    },
    {
      accessorKey: "metricValue",
      header: <div>Metric Value</div>,
      accessorFn: (row: any) => row?.metricValue ? Number(row?.metricValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => {
        const isActive = row?.status?.toLowerCase() == "active"
        return (
          <Badge
            variant="outline"
            className={cn(
              isActive
                  ? "text-green-600 border-green-600 bg-green-50"
                  : "text-red-600 border-red-600 bg-red-50",
              "rounded-full px-4 py-2"
            )}
          >
            {isActive
                ? "Active"
                : "Inactive"}
          </Badge>
        );
      },
    }
  ];

  const onExpandCollapse = () => {
    const nextExpandedState = !isAllExpanded;
  
    if (nextExpandedState) {
      tableRef.current?.expandAll();
    } else {
      tableRef.current?.collapseAll();
    }
    setIsAllExpanded(nextExpandedState);
  };

  const onExpandStateChange = (_: any, expanded: boolean) => {
    setIsAllExpanded(expanded);
  }

  return {
    breadcrumbData,
    title,
    tableData,
    columns,
    subColumns,
    level2Columns,
    onExpandCollapse,
    onExpandStateChange,
    tableRef,
    isAllExpanded,
    isMetricDataLoading
  }
}

export default useDetailedView;